import { AsyncPipe, formatDate, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DownloadTemplateButtonComponent,
    FileManagerComponent,
    NgClass,
    IconComponent,
    InputComponent,
    FormsModule,
    ReactiveFormsModule,
    AsyncPipe,
  ],
  selector: 'aux-quarter-close-add-po-report',
  templateUrl: './add-po-report-upload.component.html',
})
export class AddPoReportUploadComponent {
  ref = inject(CustomOverlayRef);

  private apiService = inject(ApiService);
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  currentOpenMonth = '';
  documentShortName = new FormControl('');
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  loading$ = new BehaviorSubject(false);
  metadata: FileMetadata = {};
  templateType = TemplateType.PURCHASE_ORDER_REPORT_TEMPLATE;

  constructor() {
    this.mainQuery
      .select('currentOpenMonth')
      .pipe(takeUntilDestroyed())
      .subscribe((month) => {
        this.currentOpenMonth = (month ? dayjs(month) : dayjs()).format('MM-DD-YYYY');
      });
  }

  getFilePath(month: string, vendor_id: string) {
    const formatted_current_month = formatDate(month, 'MMMM-y', 'en-US');
    const trialId = this.mainQuery.getValue().trialKey;

    if (vendor_id) {
      return `trials/${trialId}/vendors/${vendor_id}/po-report/${formatted_current_month}/`;
    } else {
      return `trials/${trialId}/po-report/${formatted_current_month}/`;
    }
  }

  isUploadButtonDisabled(): boolean {
    return (
      !!this.fileManager?.fileQuery.getAll() &&
      (this.fileManager?.fileQuery.getAll().length > 1 ||
        this.fileManager?.fileQuery.getAll().length === 0)
    );
  }

  async onUpload() {
    if (this.fileManager && !this.loading$.getValue()) {
      const file = this.fileManager.fileQuery.getAll()[0];

      this.loading$.next(true);

      const filePath = this.getFilePath(this.currentOpenMonth || dayjs().toString(), '');

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: `${filePath}${file.bucket_key}`,
      });

      const metadata: FileMetadata = { admin: '1' };
      const fileSuccess = await this.fileManager.fileService.uploadFiles({ metadata: metadata });

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${filePath}${file.bucket_key}`,
            entity_id: '',
            entity_type: EntityType.TRIAL,
            payload: JSON.stringify({
              document_short_name: this.documentShortName.value,
              month: this.currentOpenMonth,
            }),
            type: EventType.PURCHASE_ORDER_REPORT_TEMPLATE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success();
        } else {
          const removeFiles = async () => {
            await this.apiService.removeFile(`${filePath}${file.bucket_key}`);
          };

          removeFiles().catch((error: Error) => {
            console.error('Error removing files:', error);
          });

          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  pathFn: () => string = () => '';

  removeFile(file: File) {
    if (this.fileManager) {
      void this.fileManager.removeFile(file);
    }
  }

  uploadButtonTooltip(): string {
    if (this.fileManager?.fileQuery.getAll()) {
      if (this.fileManager?.fileQuery.getAll().length > 1) {
        return MessagesConstants.FILE.MAX_ONE_FILE;
      }

      if (this.fileManager?.fileQuery.getAll().length === 0) {
        return MessagesConstants.FILE.NEED_UPLOAD_FILE;
      }
    }

    return '';
  }
}
